import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Bot @username without the @. */
  botUsername: string;
  /** Absolute callback URL. Must have NO query string — see routes/auth.ts. */
  authUrl: string;
}

/**
 * An inline code chip.
 *
 * The old stylesheet styled the bare `code` element globally and nothing does
 * now, so the three in the hint below carry their own. `bg-surface-secondary`
 * rather than `bg-surface`: the card behind them is already `bg-surface`, and
 * the original rule leaned on its border alone to tell the two apart — which
 * reads as a box with no fill once the border is the only difference. `0.9em`
 * rather than a fixed size, so the chips track the hint's font size as before.
 */
const CODE =
  'rounded-sm border border-border bg-surface-secondary px-1 py-0.5 font-mono text-[0.9em]';

/**
 * The official Telegram Login Widget.
 *
 * It is an injected <script> that replaces itself with an iframe, so it cannot
 * be expressed as JSX — hence the imperative DOM work.
 *
 * The widget renders ONLY on the exact domain registered with `/setdomain` in
 * @BotFather. Not a parent domain, not a different subdomain, not localhost.
 * When the domain does not match, Telegram returns HTTP 200 with the body
 * "Bot domain invalid" and nothing appears — a silent failure that looks like a
 * broken app. The fallback hint below exists to make that diagnosable.
 */
export function TelegramLoginButton({ botUsername, authUrl }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  // null = still waiting; false = gave up. Keeps the hint hidden while loading
  // instead of flashing "not registered" on every first paint.
  const [rendered, setRendered] = useState<boolean | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = '';
    setRendered(null);

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?24';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    // Lets the bot message the user directly later (reminders, PRD section 8).
    // Shows an extra opt-in checkbox; harmless if never used.
    script.setAttribute('data-request-access', 'write');
    // Redirect mode: Telegram navigates to authUrl with the signed fields
    // appended as query parameters.
    script.setAttribute('data-auth-url', authUrl);

    container.appendChild(script);

    // The widget swaps itself for an iframe once Telegram accepts the domain.
    // Watch for it rather than sampling once: on a cold cache or slow mobile
    // connection the script can take several seconds, and a single timeout
    // would leave the "domain not registered" hint sitting under a perfectly
    // working Telegram button.
    const observer = new MutationObserver(() => {
      if (container.querySelector('iframe')) {
        setRendered(true);
        observer.disconnect();
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    // Backstop for the genuinely-broken case, generous enough not to fire on a
    // merely slow network.
    const timer = window.setTimeout(() => {
      setRendered(container.querySelector('iframe') !== null);
    }, 8000);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      container.innerHTML = '';
    };
  }, [botUsername, authUrl]);

  return (
    // No top margin of its own any more: LoginPage renders this inside a
    // `Card.Content` that is a `flex flex-col gap-4`, so the parent owns the
    // separation and a margin here would double it. The inner gap is only
    // between the widget and its fallback hint.
    <div className="flex flex-col gap-3">
      <div ref={containerRef} />
      {rendered === false && (
        <p className="text-sm text-muted">
          Если кнопка Telegram здесь не появилась, домен входа не зарегистрирован. В @BotFather
          выполните <code className={CODE}>/setdomain</code> для{' '}
          <code className={CODE}>@{botUsername}</code> и укажите{' '}
          <code className={CODE}>{safeHost(authUrl)}</code> — только хост, без схемы и пути.
        </p>
      )}
    </div>
  );
}

function safeHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
